import supabase from "../../supabase/supabase";
import { reportService } from "../reportService";
import { ACCOUNT_NORMAL_SIDE, normalizeAccountType } from "../coaService";

const PAGE_SIZE = 1000;
const NON_POSTED_STATUSES = new Set([
  "draft",
  "pending",
  "void",
  "voided",
  "cancelled",
  "canceled",
  "rejected",
]);

const round = (value) => Number(Number(value || 0).toFixed(2));

function requireOrganization(organizationId) {
  if (!organizationId) {
    throw new Error("Select a business before generating a report.");
  }
  return organizationId;
}

function isPosted(row) {
  return !NON_POSTED_STATUSES.has(
    String(row.status || "").trim().toLowerCase(),
  );
}

async function getAllLedgerRows(organizationId, options = {}) {
  const rows = [];
  let offset = 0;

  while (true) {
    let query = supabase
      .schema("accounting")
      .from("transactions")
      .select(
        "id,organization_id,account_id,batch_id,date,description,debit,credit,source,status,created_at",
      )
      .eq("organization_id", organizationId)
      .order("date", { ascending: true })
      .order("created_at", { ascending: true })
      .order("id", { ascending: true })
      .range(offset, offset + PAGE_SIZE - 1);

    if (options.accountId) {
      query = query.eq("account_id", options.accountId);
    }
    if (options.endDate) {
      query = query.lte("date", options.endDate);
    }

    const { data, error } = await query;
    if (error) throw error;

    const batch = data || [];
    rows.push(...batch);
    if (batch.length < PAGE_SIZE) break;
    offset += PAGE_SIZE;
  }

  return rows.filter(isPosted);
}

function normalizeAccounts(accounts) {
  return (accounts || [])
    .filter((account) => account.is_active !== false)
    .map((account) => ({
      ...account,
      type: normalizeAccountType(account.type),
    }))
    .sort((first, second) =>
      String(first.code || "").localeCompare(
        String(second.code || ""),
        undefined,
        { numeric: true },
      ) || String(first.name || "").localeCompare(String(second.name || "")),
    );
}

export const clientReportService = {
  async getClientReportAccounts(organizationId) {
    const { account_id: id, coa } =
      await reportService.getBaseData(requireOrganization(organizationId));
    if (id !== organizationId) {
      throw new Error("Report organization scope could not be verified.");
    }
    return normalizeAccounts(coa);
  },

  async getClientProfitLoss({ organizationId, startDate, endDate }) {
    const organization = requireOrganization(organizationId);
    const [base, report] = await Promise.all([
      reportService.getBaseData(organization),
      reportService.generatePL(startDate, endDate, organization),
    ]);

    if (base.account_id !== organization) {
      throw new Error("Report organization scope could not be verified.");
    }

    const expenseAccounts = normalizeAccounts(base.coa).filter(
      (account) =>
        ["Expense", "Expenses"].includes(account.type) &&
        !String(account.sub_type || "")
          .toLowerCase()
          .match(/cost of goods sold|cost of sales|cogs/),
    );
    const includedIds = new Set(
      report.accountDetails.expenses.map((account) => account.id),
    );

    if (expenseAccounts.some((account) => !includedIds.has(account.id))) {
      throw new Error(
        "The shared Profit & Loss calculation does not recognize all expense account classifications in this organization. The report is withheld to avoid incomplete totals.",
      );
    }

    return report;
  },

  async getClientBalanceSheet({ organizationId, asOfDate }) {
    return reportService.generateBalanceSheet(
      asOfDate,
      requireOrganization(organizationId),
      "accrual",
    );
  },

  async getClientCashFlow() {
    return {
      supported: false,
      reason:
        "The existing Cash Flow service reads legacy accounting tables, not this organization's canonical ledger. No Cash Flow figures are shown until a compatible shared calculation is available.",
    };
  },

  async getClientTrialBalance({ organizationId, asOfDate }) {
    const organization = requireOrganization(organizationId);
    const [accounts, ledger] = await Promise.all([
      this.getClientReportAccounts(organization),
      getAllLedgerRows(organization, { endDate: asOfDate }),
    ]);
    const totalsByAccount = new Map();

    ledger.forEach((row) => {
      const current = totalsByAccount.get(row.account_id) || {
        debit: 0,
        credit: 0,
      };
      current.debit += Number(row.debit || 0);
      current.credit += Number(row.credit || 0);
      totalsByAccount.set(row.account_id, current);
    });

    const rows = accounts.map((account) => {
      const totals = totalsByAccount.get(account.id) || {
        debit: 0,
        credit: 0,
      };
      const normalSide = ACCOUNT_NORMAL_SIDE[account.type] || "Debit";
      const balance = normalSide === "Credit"
        ? totals.credit - totals.debit
        : totals.debit - totals.credit;

      return {
        id: account.id,
        code: account.code || "",
        name: account.name,
        debit: round(balance > 0 && normalSide === "Debit"
          ? balance
          : balance < 0 && normalSide === "Credit"
            ? Math.abs(balance)
            : 0),
        credit: round(balance > 0 && normalSide === "Credit"
          ? balance
          : balance < 0 && normalSide === "Debit"
            ? Math.abs(balance)
            : 0),
      };
    });

    const totalDebit = round(rows.reduce((sum, row) => sum + row.debit, 0));
    const totalCredit = round(rows.reduce((sum, row) => sum + row.credit, 0));

    return {
      rows,
      totalDebit,
      totalCredit,
      difference: round(totalDebit - totalCredit),
      asOfDate,
      transactionCount: ledger.length,
    };
  },

  async getClientGeneralLedger({
    organizationId,
    accountId,
    startDate,
    endDate,
    search = "",
    page = 1,
    pageSize = 25,
  }) {
    const organization = requireOrganization(organizationId);
    if (!accountId) {
      throw new Error("Select an account to view its General Ledger.");
    }

    const accounts = await this.getClientReportAccounts(organization);
    const account = accounts.find((item) => item.id === accountId);
    if (!account) {
      throw new Error("The selected account is not available in this organization.");
    }

    const ledger = await getAllLedgerRows(organization, {
      accountId,
      endDate,
    });
    const normalSide = ACCOUNT_NORMAL_SIDE[account.type] || "Debit";
    let runningBalance = 0;
    let openingBalance = 0;
    const periodRows = [];

    ledger.forEach((transaction) => {
      const debit = Number(transaction.debit || 0);
      const credit = Number(transaction.credit || 0);
      runningBalance += normalSide === "Credit"
        ? credit - debit
        : debit - credit;

      if (startDate && transaction.date < startDate) {
        openingBalance = runningBalance;
        return;
      }

      if (startDate && transaction.date < startDate) return;
      if (endDate && transaction.date > endDate) return;

      periodRows.push({
        ...transaction,
        debit,
        credit,
        runningBalance: round(runningBalance),
        reference: transaction.batch_id || transaction.id,
      });
    });

    const query = search.trim().toLocaleLowerCase();
    const filtered = query
      ? periodRows.filter((row) =>
          [row.description, row.reference, row.source]
            .filter(Boolean)
            .join(" ")
            .toLocaleLowerCase()
            .includes(query),
        )
      : periodRows;
    const start = Math.max(0, (page - 1) * pageSize);

    return {
      account,
      openingBalance: round(openingBalance),
      closingBalance: round(runningBalance),
      rows: filtered.slice(start, start + pageSize),
      totalRows: filtered.length,
      page,
      pageSize,
      hasMore: start + pageSize < filtered.length,
    };
  },
};